import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import cookies from 'js-cookie'

import { ACTION_WEBSOCKET_CONNECT, ACTION_WEBSOCKET_DISCONNECT } from '@/app/definitions'
import { selectDidChange } from '@/app/module/campaigns/store/slices/campaign-item.slice'
import { getContactsPageSize } from '@/app/module/contacts/store/actions'
import { getMessagesFilters, getMessagesPageSize } from '@/app/module/logs/store/actions'
import { createNotification } from '@/app/module/notifications/store/actions'
import { getUser } from '@/app/module/user/store/actions'
import { clearCookies } from '@/app/service/util/cookie'
import { AppDispatch, RootState } from '@/app/store'

import { ERROR_GET_AUTH_CLIENT, ERROR_GET_AUTH_SERVER } from '../definitions'
import { isTokenAboutToExpire } from '../helpers'

export type SessionState = {
  error?: Record<string, unknown>
  loading: boolean
  otp: string | null
  reauthUsername: string | null
  trialMode: boolean
}

export const initialSessionState: SessionState = {
  loading: false,
  otp: null,
  reauthUsername: null,
  trialMode: false,
}

export const sessionSlice = createSlice({
  name: 'session',
  initialState: initialSessionState,
  reducers: {
    logout: (state) => state, // The actual state reset logic is handled in the root reducer, this is just a signal for the reducer to reset the state
    setError: (state, action: PayloadAction<Record<string, unknown> | undefined>) => {
      state.loading = false
      state.error = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setOtp: (state, action: PayloadAction<string | null>) => {
      state.loading = false
      state.otp = action.payload
    },
    setReauthUsername: (state, action: PayloadAction<string | null>) => {
      state.reauthUsername = action.payload
    },
    setTrialMode: (state, action: PayloadAction<boolean>) => {
      state.trialMode = action.payload
    },
  },
  selectors: {
    selectOTPToken: (state) => state.otp ?? '',
    selectReauthUsername: (state) => state.reauthUsername,
    selectSessionError: (state) => state.error,
    selectSessionLoading: (state) => state.loading,
    selectTrialMode: (state) => state.trialMode,
  },
})

const { actions, reducer, selectors } = sessionSlice

export const { logout, setError, setLoading, setOtp, setReauthUsername, setTrialMode } = actions

export const { selectOTPToken, selectReauthUsername, selectSessionError, selectSessionLoading, selectTrialMode } =
  selectors

export const getUserPreferences =
  ({ user, token }: { user: { id: string; activeOrgId: string }; token: string }) =>
  (dispatch: AppDispatch) => {
    dispatch({ type: ACTION_WEBSOCKET_CONNECT, orgId: user.activeOrgId, token })
    getMessagesFilters(user.id)(dispatch)
    getContactsPageSize()(dispatch)
    getMessagesPageSize()(dispatch)
    dispatch(createNotification({}))
  }

export const logoutUser = () => (dispatch: AppDispatch) => {
  clearCookies()
  cookies.set('logout', 'true', { sameSite: 'strict', secure: true })
  localStorage.clear()
  dispatch(logout())
  dispatch({ type: ACTION_WEBSOCKET_DISCONNECT })
}

const isCampaignPath = (pathname: string) => /^\/campaigns\/(?:create|edit)(?:\/.*)?$/.test(pathname)

const isEligibleForReauthentication = ({
  hasUnsavedChanges,
  pathname,
}: {
  hasUnsavedChanges: boolean
  pathname: string
}) => isCampaignPath(pathname) && hasUnsavedChanges

export const requestReauthentication = () => (dispatch: AppDispatch, getState: () => RootState) => {
  const { session, user } = getState()
  const currentUsername = user?.details?.data?.username
  const hasUnsavedChanges = selectDidChange(getState())
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  const reauthUsername = session?.reauthUsername

  if (reauthUsername) {
    // reauthentication is already in progress, no need to dispatch again
    return
  }

  if (!isEligibleForReauthentication({ hasUnsavedChanges, pathname }) || !currentUsername) {
    // If the current state is not eligible for reauthentication (e.g. no unsaved changes on edit campaign page) or we don't have the current username, we log out the user instead of requesting reauthentication
    dispatch(logoutUser())
    return
  }

  cookies.remove('authToken')
  cookies.remove('redirectURL')
  dispatch(setReauthUsername(currentUsername))
  dispatch({ type: ACTION_WEBSOCKET_DISCONNECT })
}

export const completeReauthentication = () => (dispatch: AppDispatch) => {
  dispatch(setReauthUsername(null))
}

export const activateTrialMode =
  ({ trialMode }: { trialMode: boolean }) =>
  (dispatch: AppDispatch) => {
    dispatch(setTrialMode(trialMode))
  }

export const initializeSession = (token: string) => async (dispatch: AppDispatch) => {
  if (!token || isTokenAboutToExpire()) {
    dispatch(logoutUser())
    return false
  }

  dispatch(setLoading(true))
  try {
    const user = await dispatch(getUser({ token }))
    if (user && !(user instanceof Error) && !('status' in user)) {
      dispatch(getUserPreferences({ user, token }))
    }
  } catch (error) {
    const err = error as {
      status?: number
      response?: { errorCode: string; error: string }
    }
    if (err.status && err.status >= 400 && err.status < 500) {
      if (err.response?.errorCode === 'login.locked') {
        dispatch(setError({ status: err.status, message: err.response.error }))
        dispatch(createNotification({ 'login-error': { type: 'error', message: err.response.error } }))
        return err
      }

      dispatch(setError({ status: err.status, message: ERROR_GET_AUTH_CLIENT }))
      dispatch(createNotification({ 'login-error': { type: 'error', message: ERROR_GET_AUTH_CLIENT } }))
      return err
    }
    dispatch(setError({ status: err.status, message: ERROR_GET_AUTH_SERVER }))
    dispatch(createNotification({ 'login-error': { type: 'error', message: ERROR_GET_AUTH_SERVER } }))
    return err
  } finally {
    dispatch(setLoading(false))
  }
}

export const selectIsReauthEligible = (state: RootState) => {
  const hasUnsavedChanges = selectDidChange(state)
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  return isEligibleForReauthentication({ hasUnsavedChanges, pathname })
}

export default reducer
